import React, { useState, useEffect } from "react";
import { ref, onValue, push, set, update } from "firebase/database";
import { db } from "./firebase";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import logoPath from "./assets/Xmaths.png";
import { formatCourseId } from "./utils/formatCourse";

const ATTENDANCE_STATUSES = ["Absent", "Attended", "Online"];

const AdminAttendance = () => {
    const [courses, setCourses] = useState([]);
    const [activeCourse, setActiveCourse] = useState("");
    const [students, setStudents] = useState({});
    const [classes, setClasses] = useState({});
    const [newClassName, setNewClassName] = useState("");
    const [newClassDate, setNewClassDate] = useState("");
    const [selectedClassId, setSelectedClassId] = useState("");
    const [attendanceDraft, setAttendanceDraft] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Fetch courses
    useEffect(() => {
        const coursesRef = ref(db, "courses");
        return onValue(coursesRef, (snapshot) => {
            if (snapshot.exists()) {
                setCourses(Object.keys(snapshot.val()));
            }
        });
    }, []);

    // Fetch students and classes for active course
    useEffect(() => {
        if (!activeCourse) return;
        setLoading(true);
        const studentsRef = ref(db, `courses/${activeCourse}/students`);
        const attendanceRef = ref(db, `courses/${activeCourse}/attendance`);
        const unsubStudents = onValue(studentsRef, (snap) => {
            if (snap.exists()) {
                const studentsData = snap.val();
                // Sort students alphabetically by name
                const sortedStudents = Object.fromEntries(
                    Object.entries(studentsData).sort(([, a], [, b]) =>
                        (a.name || '').localeCompare(b.name || '')
                    )
                );
                setStudents(sortedStudents);
            } else {
                setStudents({});
            }
        });
        const unsubAttendance = onValue(attendanceRef, (snap) => {
            setClasses(snap.exists() ? snap.val() : {});
            setLoading(false);
        });
        return () => { unsubStudents(); unsubAttendance(); };
    }, [activeCourse]);

    // Add new class
    const handleAddClass = async () => {
        if (!newClassName.trim() || !newClassDate) return;
        const attendanceRef = ref(db, `courses/${activeCourse}/attendance`);
        const newClassRef = push(attendanceRef);
        const classId = newClassRef.key;
        // Only save className and date under the class node
        await set(newClassRef, {
            className: newClassName.trim(),
            date: newClassDate
        });
        // Save each student's attendance under their own node
        for (const sid of Object.keys(students)) {
            await set(ref(db, `courses/${activeCourse}/students/${sid}/attendance/${classId}`), {
                className: newClassName.trim(),
                date: newClassDate,
                status: "Absent"
            });
        }
        setNewClassName("");
        setNewClassDate("");
    };

    // When a class is selected, load its attendance into draft from the students' nodes
    useEffect(() => {
        if (!selectedClassId || !classes[selectedClassId]) {
            setAttendanceDraft({});
            return;
        }
        // Gather attendance from each student's node
        const draft = {};
        Object.entries(students).forEach(([sid, s]) => {
            const att = s.attendance && s.attendance[selectedClassId];
            draft[sid] = att ? att.status : "Absent";
        });
        setAttendanceDraft(draft);
    }, [selectedClassId, classes, students]);

    // Change attendance status in draft
    const handleAttendanceChange = (studentId, status) => {
        setAttendanceDraft(draft => ({ ...draft, [studentId]: status }));
    };

    // Save attendance changes: only update under each student's node
    const handleSaveAttendance = async () => {
        if (!selectedClassId) return;
        for (const [sid, status] of Object.entries(attendanceDraft)) {
            await update(ref(db, `courses/${activeCourse}/students/${sid}/attendance/${selectedClassId}`), {
                status
            });
        }
    };

    // PDF Export for selected class
    const exportToPDF = () => {
        if (!selectedClassId || !classes[selectedClassId]) return;
        const classObj = classes[selectedClassId];
        const doc = new jsPDF();
        const img = new window.Image();
        img.src = logoPath;
        img.onload = () => {
            doc.addImage(img, "PNG", 10, 10, 30, 30);
            doc.setFontSize(20);
            doc.text("Class Attendance Report", 60, 20);
            doc.setFontSize(12);
            doc.text(`Course: ${activeCourse}`, 60, 30);
            doc.text(`Class: ${classObj.className} (${classObj.date})`, 60, 36);

            // Sort students alphabetically for PDF
            const sortedStudents = Object.entries(students).sort(([, a], [, b]) =>
                (a.name || '').localeCompare(b.name || '')
            );

            const tableData = sortedStudents.map(([sid, s]) => [
                sid,
                s.name || sid,
                attendanceDraft[sid] || "Absent"
            ]);

            autoTable(doc, {
                head: [["Student ID", "Name", "Status"]],
                body: tableData,
                startY: 45,
                theme: 'grid',
                styles: {
                    fontSize: 10,
                    cellPadding: 5,
                },
                columnStyles: {
                    0: { cellWidth: 25 },
                    1: { cellWidth: 'auto' },
                    2: { cellWidth: 30 }
                }
            });
            doc.save(`${activeCourse}_${classObj.className}_${classObj.date}_attendance.pdf`);
        };
    };

    return (
        <div className="p-4 min-h-screen text-black">
            <h1 className="text-3xl font-bold mb-6">Attendance Management</h1>
            {/* Course Selector */}
            <div className="mb-6">
                <label className="block mb-2 font-medium">Select Course:</label>
                <select
                    value={activeCourse}
                    onChange={e => { setActiveCourse(e.target.value); setSelectedClassId(""); }}
                    className="p-2 border rounded-md"
                >
                    <option value="">Select course</option>
                    {courses.map(c => <option key={c} value={c}>{formatCourseId(c)}</option>)}
                </select>
            </div>
            {/* Add Class */}
            {activeCourse && (
                <div className="mb-8 bg-white p-4 rounded-lg shadow-md">
                    <h2 className="text-lg font-semibold mb-2">Add New Class</h2>
                    <div className="flex flex-col sm:flex-row gap-2 items-center">
                        <input
                            type="text"
                            placeholder="Class name"
                            value={newClassName}
                            onChange={e => setNewClassName(e.target.value)}
                            className="p-2 border rounded-md flex-1"
                        />
                        <input
                            type="date"
                            value={newClassDate}
                            onChange={e => setNewClassDate(e.target.value)}
                            className="p-2 border rounded-md flex-1"
                        />
                        <button
                            onClick={handleAddClass}
                            className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700"
                        >
                            Add Class
                        </button>
                    </div>
                </div>
            )}
            {/* Classes List/Dropdown */}
            {activeCourse && Object.keys(classes).length > 0 && (
                <div className="mb-8">
                    <label className="block mb-2 font-medium">Select Class:</label>
                    <select
                        value={selectedClassId}
                        onChange={e => setSelectedClassId(e.target.value)}
                        className="p-2 border rounded-md"
                    >
                        <option value="">Select class</option>
                        {Object.entries(classes).map(([classId, classObj]) => (
                            <option key={classId} value={classId}>
                                {classObj.className} ({classObj.date})
                            </option>
                        ))}
                    </select>
                </div>
            )}
            {/* Students Table for Selected Class */}
            {selectedClassId && classes[selectedClassId] && (
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="flex justify-between items-center px-6 pt-6">
                        <h2 className="text-lg font-semibold mb-4">
                            {classes[selectedClassId].className} - {classes[selectedClassId].date}
                        </h2>
                        <button
                            onClick={exportToPDF}
                            className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 transition-colors"
                        >
                            Export to PDF
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Student ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {Object.entries(students)
                                    .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || '')) // Sort alphabetically
                                    .map(([sid, s], idx) => {
                                        const status = attendanceDraft[sid] || "Absent";
                                        let statusBg = "";
                                        let statusText = "";
                                        if (status === "Attended" || status === "Online") {
                                            statusBg = "bg-green-50";
                                            statusText = "text-green-700";
                                        } else if (status === "Absent") {
                                            statusBg = "bg-red-50";
                                            statusText = "text-red-700";
                                        }
                                        return (
                                            <tr key={sid} className={`hover:bg-teal-50 transition`}>
                                                <td className="px-6 py-3 font-mono text-xs text-gray-500">{sid}</td>
                                                <td className="px-6 py-3">{s.name || sid}</td>
                                                <td className="px-6 py-3">
                                                    <select
                                                        value={status}
                                                        onChange={e => handleAttendanceChange(sid, e.target.value)}
                                                        className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-teal-400 ${statusBg} ${statusText}`}
                                                    >
                                                        <option value="Attended" className="text-green-700 bg-green-50">Attended</option>
                                                        <option value="Online" className="text-green-700 bg-green-50">Online</option>
                                                        <option value="Absent" className="text-red-700 bg-red-50">Absent</option>
                                                    </select>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                    <div className="px-6 pb-6 pt-4">
                        <button
                            onClick={handleSaveAttendance}
                            className="bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 transition-colors"
                        >
                            Save Attendance
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAttendance; 